import React from 'react';
import { track, EVENTS } from '../services/analytics';

// Same slugging discipline as SourceLink's sourceKey — `law` is a small fixed
// set chosen by the call site, never user or document text.
const lawKey = (law) => String(law || 'law')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'law';

/**
 * RegulationCitation — a small, muted footnote naming the actual law behind
 * a privacy promise (e.g. "Backed by: CCPA - Cal. Civ. Code Section 1798.105(a) up-arrow").
 *
 * Same visual register as SourceLink.jsx (11.5px muted text, green underlined
 * external link) so a citation reads as the same kind of claim as a data
 * source, not a louder one.
 *
 * NOT a compliance claim. Each `refs` entry states what a specific law
 * requires or defines - it does not assert Stratia is a covered entity
 * under that law, or that Stratia is certified/compliant with it as a
 * whole. Some of these laws (SOPIPA's "operator" of a K-12 school service,
 * COPPA's rules for services directed at children under 13) are written for
 * a category Stratia may not technically sit inside; we hold the same line
 * anyway and say so as a fact, not a certification. Callers must not add a
 * reference whose framing implies broader legal coverage than that.
 *
 * @param {Array<{law: string, cite: string, url: string}>} refs - each a
 *   short law name ("CCPA"), the specific citation ("Cal. Civ. Code Section
 *   1798.105(a)"), and the official register URL it links to.
 * @param {string} className - optional extra classes on the root span.
 * @param {string} surface   - where this citation renders, for analytics
 *   (mirrors SourceLink's surface prop).
 */
const RegulationCitation = ({ refs, className = '', surface = 'unknown' }) => {
    if (!refs || refs.length === 0) return null;

    const onClick = (law) => () => {
        track(EVENTS.REGULATION_CITATION_CLICKED, { law: lawKey(law), surface });
    };

    return (
        <span
            className={`text-[11.5px] text-[#8A8A8A] ${className}`.trim()}
            data-testid="regulation-citation"
        >
            Backed by:{' '}
            {refs.map((r, i) => (
                <React.Fragment key={r.law}>
                    {i > 0 && ' · '}
                    <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClick(r.law)}
                        className="text-[#1A4D2E] underline hover:no-underline"
                    >
                        {r.law} - {r.cite} ↗
                    </a>
                </React.Fragment>
            ))}
        </span>
    );
};

export default RegulationCitation;
